import React, { useEffect, useState } from 'react';
import { SearchIcon, FilterIcon, DownloadIcon, MessageSquareIcon, EyeIcon, PlusIcon, AlertCircleIcon, ClockIcon, CheckCircleIcon, XIcon } from 'lucide-react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { useAuth } from '../context/AuthContext';
import { getTicketsByMerchant, SupportTicket } from '../components/support/SupportTicketStore';
const SupportTicketsPage = () => {
  const {
    user
  } = useAuth();
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('All');
  const [filterType, setFilterType] = useState('All');
  const [selectedTicket, setSelectedTicket] = useState<SupportTicket | null>(null);
  const [showModal, setShowModal] = useState(false);
  useEffect(() => {
    // Load tickets for the current merchant
    const merchantId = user?.merchantId || '050102100023650';
    setTickets(getTicketsByMerchant(merchantId));
  }, [user]);
  // Filter tickets
  const filteredTickets = tickets.filter(ticket => {
    const matchesSearch = ticket.id.toLowerCase().includes(searchTerm.toLowerCase()) || ticket.category.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'All' || filterStatus === 'Open' && ['Open', 'Under Review'].includes(ticket.status) || filterStatus === 'Closed' && ['Resolved', 'Closed'].includes(ticket.status);
    const matchesType = filterType === 'All' || filterType === 'Complaint' && ticket.type === 'complaint' || filterType === 'Terminal Request' && ticket.type === 'terminal-request' || filterType === 'Device Issue' && ticket.type === 'device-issue';
    return matchesSearch && matchesStatus && matchesType;
  });
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'Open':
        return <span className="px-2.5 py-1 rounded-lg bg-blue-50 text-blue-700 border border-blue-200 text-xs font-bold flex items-center" data-id="element-1980">
            <AlertCircleIcon className="w-3.5 h-3.5 mr-1" data-id="element-1981" /> Open
          </span>;
      case 'Under Review':
        return <span className="px-2.5 py-1 rounded-lg bg-amber-50 text-amber-700 border border-amber-200 text-xs font-bold flex items-center" data-id="element-1982">
            <ClockIcon className="w-3.5 h-3.5 mr-1" data-id="element-1983" /> Under Review
          </span>;
      case 'Resolved':
      case 'Closed':
        return <span className="px-2.5 py-1 rounded-lg bg-emerald-50 text-emerald-700 border border-emerald-200 text-xs font-bold flex items-center" data-id="element-1984">
            <CheckCircleIcon className="w-3.5 h-3.5 mr-1" data-id="element-1985" /> {status}
          </span>;
      default:
        return <span className="px-2.5 py-1 rounded-lg bg-slate-50 text-slate-700 border border-slate-200 text-xs font-bold" data-id="element-1986">
            {status}
          </span>;
    }
  };
  const getPriorityBadge = (priority: string) => {
    switch (priority) {
      case 'Critical':
        return <span className="px-2 py-0.5 rounded text-xs font-bold text-red-700 bg-red-50" data-id="element-1987">
            Critical
          </span>;
      case 'High':
        return <span className="px-2 py-0.5 rounded text-xs font-bold text-orange-700 bg-orange-50" data-id="element-1988">
            High
          </span>;
      case 'Medium':
        return <span className="px-2 py-0.5 rounded text-xs font-bold text-blue-700 bg-blue-50" data-id="element-1989">
            Medium
          </span>;
      case 'Low':
        return <span className="px-2 py-0.5 rounded text-xs font-bold text-slate-700 bg-slate-50" data-id="element-1990">
            Low
          </span>;
      default:
        return null;
    }
  };
  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'complaint':
        return 'Complaint';
      case 'terminal-request':
        return 'Terminal Request';
      case 'device-issue':
        return 'Device Issue';
      default:
        return 'Support Request';
    }
  };
  const openDetails = (ticket: SupportTicket) => {
    setSelectedTicket(ticket);
    setShowModal(true);
  };
  return <DashboardLayout data-id="element-1991">
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-1992">
        <div data-id="element-1993">
          <h1 className="text-2xl font-bold text-slate-900" data-id="element-1994">
            Support & Requests
          </h1>
          <p className="text-slate-500 font-medium mt-1" data-id="element-1995">
            Track and manage your support tickets and service requests
          </p>
        </div>
        <button className="flex items-center px-4 py-2.5 bg-indigo-600 text-white rounded-xl font-medium hover:bg-indigo-700 transition-colors shadow-sm" onClick={() => {
        // In a real app, this might open the chatbot or a new ticket form
        alert('Please use the AI Support Assistant (floating button) to create a new request.');
      }} data-id="element-1996">
          <PlusIcon className="h-5 w-5 mr-2" data-id="element-1997" />
          New Request
        </button>
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-0 overflow-hidden mb-6" data-id="element-1998">
        {/* Filters */}
        <div className="p-6 border-b border-slate-100 flex flex-col lg:flex-row gap-4" data-id="element-1999">
          <div className="flex-1 relative" data-id="element-2000">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-2001">
              <SearchIcon className="h-5 w-5 text-slate-400" data-id="element-2002" />
            </div>
            <input type="text" placeholder="Search by Ticket ID or Category..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="w-full pl-10 pr-4 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500 bg-slate-50 focus:bg-white transition-all text-sm" data-id="element-2003" />
          </div>

          <div className="flex flex-wrap gap-3" data-id="element-2004">
            <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="px-4 py-2.5 border border-slate-200 rounded-xl text-sm font-medium text-slate-700 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500/50 cursor-pointer" data-id="element-2005">
              <option value="All" data-id="element-2006">All Statuses</option>
              <option value="Open" data-id="element-2007">Open / Review</option>
              <option value="Closed" data-id="element-2008">Resolved / Closed</option>
            </select>

            <select value={filterType} onChange={e => setFilterType(e.target.value)} className="px-4 py-2.5 border border-slate-200 rounded-xl text-sm font-medium text-slate-700 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500/50 cursor-pointer" data-id="element-2009">
              <option value="All" data-id="element-2010">All Request Types</option>
              <option value="Complaint" data-id="element-2011">Complaints</option>
              <option value="Terminal Request" data-id="element-2012">Terminal Requests</option>
              <option value="Device Issue" data-id="element-2013">Device Issues</option>
            </select>

            <button className="p-2.5 border border-slate-200 rounded-xl text-slate-500 hover:bg-slate-50 hover:text-indigo-600 transition-colors" title="Export" data-id="element-2014">
              <DownloadIcon className="h-5 w-5" data-id="element-2015" />
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto" data-id="element-2016">
          <table className="w-full text-sm text-left" data-id="element-2017">
            <thead className="text-xs text-slate-500 uppercase tracking-wider bg-slate-50 border-b border-slate-200" data-id="element-2018">
              <tr data-id="element-2019">
                <th className="px-6 py-4 font-semibold" data-id="element-2020">Ticket ID</th>
                <th className="px-6 py-4 font-semibold" data-id="element-2021">Request Type</th>
                <th className="px-6 py-4 font-semibold" data-id="element-2022">Issue Category</th>
                <th className="px-6 py-4 font-semibold" data-id="element-2023">Created Date</th>
                <th className="px-6 py-4 font-semibold" data-id="element-2024">Status</th>
                <th className="px-6 py-4 font-semibold" data-id="element-2025">Support Team</th>
                <th className="px-6 py-4 font-semibold text-right" data-id="element-2026">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100" data-id="element-2027">
              {filteredTickets.length > 0 ? filteredTickets.map(ticket => <tr key={ticket.id} className="hover:bg-slate-50/50 transition-colors" data-id="element-2028">
                    <td className="px-6 py-4" data-id="element-2029">
                      <div className="font-bold text-indigo-900" data-id="element-2030">
                        {ticket.id}
                      </div>
                      <div className="mt-1" data-id="element-2031">
                        {getPriorityBadge(ticket.priority)}
                      </div>
                    </td>
                    <td className="px-6 py-4 font-medium text-slate-700" data-id="element-2032">
                      {getTypeLabel(ticket.type)}
                    </td>
                    <td className="px-6 py-4" data-id="element-2033">
                      <div className="font-semibold text-slate-900" data-id="element-2034">
                        {ticket.category}
                      </div>
                      <div className="text-xs text-slate-500 mt-0.5 truncate max-w-[200px]" data-id="element-2035">
                        {ticket.description}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-slate-500 font-medium" data-id="element-2036">
                      {ticket.createdDate.split(' ')[0]}
                    </td>
                    <td className="px-6 py-4" data-id="element-2037">
                      {getStatusBadge(ticket.status)}
                    </td>
                    <td className="px-6 py-4 text-slate-600 font-medium" data-id="element-2038">
                      {ticket.supportTeam}
                    </td>
                    <td className="px-6 py-4 text-right" data-id="element-2039">
                      <button onClick={() => openDetails(ticket)} className="inline-flex items-center px-3 py-1.5 bg-white border border-slate-200 rounded-lg text-sm font-medium text-indigo-600 hover:bg-indigo-50 hover:border-indigo-200 transition-colors" data-id="element-2040">
                        <EyeIcon className="h-4 w-4 mr-1.5" data-id="element-2041" /> View
                      </button>
                    </td>
                  </tr>) : <tr data-id="element-2042">
                  <td colSpan={7} className="px-6 py-16 text-center" data-id="element-2043">
                    <div className="flex flex-col items-center justify-center" data-id="element-2044">
                      <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mb-4" data-id="element-2045">
                        <MessageSquareIcon className="h-8 w-8 text-slate-400" data-id="element-2046" />
                      </div>
                      <h3 className="text-lg font-bold text-slate-900 mb-1" data-id="element-2047">
                        No tickets found
                      </h3>
                      <p className="text-slate-500 font-medium max-w-sm" data-id="element-2048">
                        {searchTerm || filterStatus !== 'All' || filterType !== 'All' ? "Try adjusting your filters or search term to find what you're looking for." : "You don't have any support tickets or requests yet."}
                      </p>
                    </div>
                  </td>
                </tr>}
            </tbody>
          </table>
        </div>
      </div>

      {/* Ticket Details Modal */}
      {showModal && selectedTicket && <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-200" data-id="element-2049">
          <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col overflow-hidden animate-in zoom-in-95 duration-200" data-id="element-2050">
            <div className="flex items-center justify-between p-6 border-b border-slate-100 bg-slate-50/50" data-id="element-2051">
              <div data-id="element-2052">
                <div className="flex items-center space-x-3 mb-1" data-id="element-2053">
                  <h2 className="text-xl font-bold text-slate-900" data-id="element-2054">
                    {selectedTicket.id}
                  </h2>
                  {getStatusBadge(selectedTicket.status)}
                </div>
                <p className="text-sm font-medium text-slate-500" data-id="element-2055">
                  {getTypeLabel(selectedTicket.type)} • Created on{' '}
                  {selectedTicket.createdDate}
                </p>
              </div>
              <button onClick={() => setShowModal(false)} className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-xl transition-colors" data-id="element-2056">
                <XIcon className="h-6 w-6" data-id="element-2057" />
              </button>
            </div>

            <div className="p-6 overflow-y-auto flex-1" data-id="element-2058">
              <div className="space-y-6" data-id="element-2059">
                {/* Overview */}
                <div data-id="element-2060">
                  <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider mb-3" data-id="element-2061">
                    Issue Overview
                  </h3>
                  <div className="bg-slate-50 rounded-xl p-4 border border-slate-100" data-id="element-2062">
                    <div className="grid grid-cols-2 gap-4 mb-4" data-id="element-2063">
                      <div data-id="element-2064">
                        <p className="text-xs font-medium text-slate-500 mb-1" data-id="element-2065">
                          Category
                        </p>
                        <p className="font-semibold text-slate-900" data-id="element-2066">
                          {selectedTicket.category}
                        </p>
                      </div>
                      <div data-id="element-2067">
                        <p className="text-xs font-medium text-slate-500 mb-1" data-id="element-2068">
                          Priority
                        </p>
                        <div data-id="element-2069">{getPriorityBadge(selectedTicket.priority)}</div>
                      </div>
                    </div>
                    <div data-id="element-2070">
                      <p className="text-xs font-medium text-slate-500 mb-1" data-id="element-2071">
                        Description
                      </p>
                      <p className="text-sm text-slate-700 leading-relaxed" data-id="element-2072">
                        {selectedTicket.description}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Specific Details */}
                <div data-id="element-2073">
                  <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider mb-3" data-id="element-2074">
                    Request Details
                  </h3>
                  <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-id="element-2075">
                    <dl className="divide-y divide-slate-100" data-id="element-2076">
                      {Object.entries(selectedTicket.details).map(([key, value]) => <div key={key} className="flex justify-between px-4 py-3" data-id="element-2077">
                            <dt className="text-sm font-medium text-slate-500 capitalize" data-id="element-2078">
                              {key.replace(/([A-Z])/g, ' $1').trim()}
                            </dt>
                            <dd className="text-sm font-semibold text-slate-900 text-right max-w-[60%]" data-id="element-2079">
                              {value}
                            </dd>
                          </div>)}
                    </dl>
                  </div>
                </div>

                {/* Resolution Info */}
                <div data-id="element-2080">
                  <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider mb-3" data-id="element-2081">
                    Resolution Status
                  </h3>
                  <div className="bg-indigo-50 border border-indigo-100 rounded-xl p-4" data-id="element-2082">
                    <div className="grid grid-cols-2 gap-4" data-id="element-2083">
                      <div data-id="element-2084">
                        <p className="text-xs font-medium text-indigo-400 mb-1" data-id="element-2085">
                          Assigned Team
                        </p>
                        <p className="font-semibold text-indigo-900" data-id="element-2086">
                          {selectedTicket.supportTeam}
                        </p>
                      </div>
                      <div data-id="element-2087">
                        <p className="text-xs font-medium text-indigo-400 mb-1" data-id="element-2088">
                          Last Updated
                        </p>
                        <p className="font-semibold text-indigo-900" data-id="element-2089">
                          {selectedTicket.lastUpdated}
                        </p>
                      </div>
                      <div className="col-span-2 pt-2 mt-2 border-t border-indigo-100/50" data-id="element-2090">
                        <p className="text-xs font-medium text-indigo-400 mb-1" data-id="element-2091">
                          Expected Resolution
                        </p>
                        <p className="font-semibold text-indigo-900" data-id="element-2092">
                          {selectedTicket.expectedResolution || 'Pending assessment'}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-slate-100 bg-slate-50 flex justify-end gap-3" data-id="element-2093">
              <button onClick={() => setShowModal(false)} className="px-5 py-2.5 border border-slate-300 rounded-xl text-sm font-medium text-slate-700 hover:bg-slate-100 transition-colors" data-id="element-2094">
                Close
              </button>
              {selectedTicket.status !== 'Closed' && selectedTicket.status !== 'Resolved' && <button className="px-5 py-2.5 bg-indigo-600 text-white rounded-xl text-sm font-medium hover:bg-indigo-700 transition-colors shadow-sm" data-id="element-2095">
                    Add Comment
                  </button>}
            </div>
          </div>
        </div>}
    </DashboardLayout>;
};
export default SupportTicketsPage;